import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Link2, X, Plus, ShieldAlert, ArrowRight, Lock } from "lucide-react";
import { statusDotClasses } from "@/lib/format";
import { toast } from "sonner";

export default function DependenciesSection({ task, list, allTasks, canEdit, onOpenTask, onChange }) {
  const [data, setData] = useState({ dependencies: [], inverse: [], tasks: [] });
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [relation, setRelation] = useState("blocked_by");

  const statusMap = Object.fromEntries((list.statuses || []).map((s) => [s.id, s]));

  const refresh = async () => {
    try {
      const { data: d } = await api.get(`/tasks/${task.id}/dependencies`);
      setData(d);
    } catch { /* ignore */ }
  };

  useEffect(() => { refresh(); }, [task.id]);

  const taskById = Object.fromEntries(data.tasks.map((t) => [t.id, t]));

  const addDep = async (otherId) => {
    try {
      await api.post(`/tasks/${task.id}/dependencies`, { depends_on_task_id: otherId, relation });
      setOpen(false);
      setQuery("");
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link");
    }
  };

  const removeDep = async (depId) => {
    try {
      await api.delete(`/dependencies/${depId}`);
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    }
  };

  // Candidates: tasks in same list, not self, not already linked
  const linkedIds = new Set([
    ...data.dependencies.map((d) => d.depends_on_task_id),
    ...data.inverse.map((d) => d.task_id),
    task.id,
  ]);
  const candidates = allTasks
    .filter((t) => !linkedIds.has(t.id))
    .filter((t) => !query || t.title.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 10);

  const renderRow = (otherId, depId, relLabel) => {
    const o = taskById[otherId];
    if (!o) return null;
    const st = statusMap[o.status_id];
    return (
      <div key={depId || otherId} className="flex items-center gap-2 px-3 py-1.5 group" data-testid={`dep-row-${depId}`}>
        <Lock className="w-3 h-3 text-neutral-400" />
        <span className="font-mono-data text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">{relLabel}</span>
        <button onClick={() => onOpenTask(otherId)} className="flex items-center gap-1.5 text-sm hover:underline truncate flex-1 text-left">
          <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotClasses(st?.color || "neutral")}`} />
          <span className="truncate">{o.title}</span>
        </button>
        {canEdit && depId && (
          <button onClick={() => removeDep(depId)} className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-600" data-testid={`dep-remove-${depId}`}>
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  return (
    <section data-testid="dependencies-section">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1">
          <Link2 className="w-3 h-3" /> Dependencies
        </div>
        {canEdit && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-7 rounded-sm text-xs" data-testid="add-dependency-button">
                <Plus className="w-3 h-3 mr-1" />Link task
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-2" align="end">
              <div className="flex gap-1 mb-2">
                {[
                  { k: "blocked_by", label: "Blocked by" },
                  { k: "blocks", label: "Blocks" },
                ].map((r) => (
                  <button
                    key={r.k}
                    onClick={() => setRelation(r.k)}
                    className={`flex-1 px-2 py-1 text-[11px] font-semibold rounded-sm transition-colors ${relation === r.k ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-700"}`}
                    data-testid={`dep-relation-${r.k}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find a task…"
                className="h-8 rounded-sm text-sm"
                data-testid="dep-search-input"
              />
              <div className="mt-2 max-h-60 overflow-y-auto">
                {candidates.length === 0 ? (
                  <div className="px-2 py-3 text-xs text-neutral-500 text-center">No matching tasks.</div>
                ) : candidates.map((c) => {
                  const st = statusMap[c.status_id];
                  return (
                    <button
                      key={c.id}
                      onClick={() => addDep(c.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-neutral-100 text-left"
                      data-testid={`dep-candidate-${c.id}`}
                    >
                      <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotClasses(st?.color || "neutral")}`} />
                      <span className="text-xs truncate">{c.title}</span>
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <div className="border border-neutral-200 rounded-sm divide-y divide-neutral-100 bg-white">
        {data.dependencies.length === 0 && data.inverse.length === 0 && (
          <div className="px-3 py-3 text-xs text-neutral-500 text-center">No dependencies.</div>
        )}
        {data.dependencies.map((d) =>
          renderRow(d.depends_on_task_id, d.id, d.relation === "blocked_by" ? "Blocked by" : "Blocks")
        )}
        {data.inverse.map((d) =>
          renderRow(d.task_id, null, d.relation === "blocked_by" ? "Blocks this" : "Blocked by this")
        )}
      </div>
    </section>
  );
}
